<template>
  <span>
    <v-autocomplete
      v-debounce:700="searchItems"
      v-model="company"
      prepend-icon="mdi-briefcase"
      :disabled="disabled"
      :return-object="true"
      :items="companies"
      :item-text="`name`"
      :item-value="`id`"
      :loading="companiesRequest.loading"
      :clearable="!!company"
      :label="label ? label : $lang('companies.title')"
      :error="!!errors.length"
      :error-messages="errors"
      :dense="dense"
      no-filter
    >
      <template v-slot:item="{ item }">
        <v-list-item-avatar style="min-width: 60px;" v-if="!!item.country">
          <country-flag :country="item.country" size="normal"/>
        </v-list-item-avatar>
        <v-list-item-content>
          <v-list-item-title>{{ item.name }}</v-list-item-title>
          <v-list-item-subtitle>
            <span v-if="!!item.phone">
              {{ $phoneFormat(item.phone) }}
            </span>
          </v-list-item-subtitle>
          <v-list-item-subtitle>{{ item.email }}</v-list-item-subtitle>
        </v-list-item-content>
      </template>
      <template v-slot:no-data>
        <div v-if="!!searchTerm"></div>
        <v-list-item v-if="!searchTerm">
          <v-list-item-content>
            <v-list-item-title>{{ $lang('clients.hints.search') }}</v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </template>
    </v-autocomplete>
    <slot name="info" :info="info">
      <v-list v-if="company" three-line>
        <v-list-item class="ml-0 pl-0">
          <v-list-item-icon class="mr-3">
            <v-icon v-if="item.is_validated_data" color="green">mdi-check-decagram</v-icon>
            <v-icon v-else color="warning">mdi-alert-decagram</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ info.details }}</v-list-item-title>
            <v-list-item-subtitle>{{ info.subDetails }}</v-list-item-subtitle>
            <v-list-item-subtitle>{{ info.accountingDetails }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-divider v-if="!!info.phone"></v-divider>
        <v-list-item v-if="!!info.phone" class="ml-0 pl-0">
          <v-list-item-icon class="mr-3">
            <v-icon color="green">mdi-whatsapp</v-icon>
          </v-list-item-icon>
          <v-list-item-content class="ml-0 pl-0">
            <v-list-item-title>
              <a :href="`https://wa.me/${$phoneFormat(info.phone)}`" target="_blank">
                <span>{{ $phoneFormat(info.phone) }}</span>
              </a>
            </v-list-item-title>
            <v-list-item-title>
              <a :href="`mailto:${info.email}`" target="_blank" class="text-overline">
                <span>{{ info.email }}</span>
              </a>
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>
    </slot>

  </span>
</template>

<script>
  const companySelects = [
    'id',
    'name',
    'phone',
    'email',
    'country',
    'city',
    'address',
    'state',
    'postcode',
    'vat_code',
    'tax_code',
    'locale',
    'sdi',
    'pec',
    'is_validated_data',
  ];
  import { mapState } from "vuex";

  import {
    GET_COMPANIES_COLLECTION,
  } from "@/store/companies/actions";

  export default {
    name: "VCompany",
    props: {
      item: {
        required: false,
      },
      disabled: {
        type: Boolean,
        required: false,
        default: false
      },
      withEdit: {
        type: Boolean,
        required: false,
        default: true
      },
      errors: {
        type: Array,
        required: false,
        default() {
          return [];
        }
      },
      dense: {
        type: Boolean,
        required: false,
        default: false
      },
      label: {
        type: String,
        required: false,
      }
    },
    data() {
      return {
        searchTerm: null,
        company: null,
        companies: [],
      }
    },
    beforeMount() {
      this.initCompany();
    },
    methods: {
      initCompany() {
        if (this.item && this.item.id) {
          this.company = this.item;
          this.companies = [this.item];
        }
      },
      searchItems(search) {
        this.searchTerm = search;
        let filter = {
          limit: 30,
          sortBy: 'name',
          order: 'asc',
          search,
          _data: {
            selects: companySelects
          }
        };

        this.$store.dispatch(GET_COMPANIES_COLLECTION, filter);
      },
    },
    computed: {
      info() {
        if (!this.company) {
          return {
            details: '---',
            subDetails: '',
            accountingDetails: '',
          }
        }

        let details = [];
        let subDetails = [];
        let accountingDetails = [];

        if (this.company.city) {
          details.push(this.company.city);
        }
        if (this.company.state) {
          details.push(this.company.state);
        }
        if (this.company.country) {
          details.push(this.company.country);
        }

        if (!details.length) {
          details = '---';
        } else {
          details = details.join(', ');
        }

        if (this.company.address) {
          subDetails.push(this.company.address);
        }

        if (this.company.sdi) {
          accountingDetails.push(`SDI: ${this.company.sdi}`);
        }

        if (this.company.vat_code) {
          accountingDetails.push(`${this.$lang('companies.fields.vat_code')} ${this.company.vat_code}`);
          accountingDetails.push(`${this.$lang('companies.fields.tax_code')} ${this.company.tax_code}`);
        }

        subDetails = subDetails.join(', ');
        accountingDetails = accountingDetails.join(', ');

        return {
          ...this.company,
          details,
          subDetails,
          accountingDetails,
        }
      },
      ...mapState({
        companiesRequest: state => state.companies.getCollectionRequest,
      })
    },
    watch: {
      'item.id'() {
        this.initCompany();
      },
      'company.id'() {
        this.$emit('changed', this.company);
      },
      'companiesRequest.loading'(val) {
        if (!val) {
          this.companies = this.companiesRequest.collection;
        }
      }
    }
  }
</script>

<style scoped>
  .v-autocomplete {
    margin-top: 0 !important;
  }
</style>
